import React, { useState, useMemo, useEffect } from "react";
import {
  UsersIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  PlusCircleIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

// --- GLOBAL MOCK DATA ---
const MOCK_EMPLOYEES = [
  { id: 'E1001', name: "Arjun Singh", designation: "Sr. Engineer", department: "Tech" },
  { id: 'E1002', name: "Priya Sharma", designation: "HR Manager", department: "HR" },
  { id: 'E1003', name: "Vikram Kumar", designation: "Sales Associate", department: "Sales" },
  { id: 'E1004', name: "Ravi Menon", designation: "Jr. Analyst", department: "Finance" },
  { id: 'E1005', name: "Sonia Das", designation: "Marketing Lead", department: "Marketing" },
  { id: 'E1006', name: "Alok Gupta", designation: "Designer", department: "Creative" },
];

const INITIAL_PROJECTS = [
  {
    id: 'P001',
    name: 'Q4 CRM Migration',
    description: 'Migrating legacy CRM data to Salesforce platform for improved client tracking.',
    startDate: '2025-11-01',
    endDate: '2025-12-31',
    status: 'In Progress',
    teamLeadId: 'E1001',
    team: [{ id: 'E1004', name: 'Ravi Menon' }, { id: 'E1006', name: 'Alok Gupta' }],
    updates: [{ date: '2025-11-20', note: 'Data mapping 80% complete.' }],
    progress: 60,
  },
];

const STATUS_OPTIONS = ['Pending', 'In Progress', 'On Hold', 'Completed'];

// --- UTILITIES ---
const getTodayDate = () => new Date().toISOString().split('T')[0];
const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
const getLeadName = (leadId) => MOCK_EMPLOYEES.find(e => e.id === leadId)?.name || 'Unassigned';

// --- COMPONENTS ---

const MetricCard = ({ title, value, icon: Icon, colorClass }) => (
  <div className="bg-white p-4 rounded-xl border border-gray-100 flex items-center space-x-3 shadow-sm">
    <div className={`p-2 rounded-lg bg-opacity-10 ${colorClass.replace('text', 'bg')}`}>
      <Icon className={`h-5 w-5 ${colorClass}`} />
    </div>
    <div>
      <p className="text-xs font-medium text-gray-500 font-roboto">{title}</p>
      <p className="text-xl font-bold text-gray-900 font-poppins">{value}</p>
    </div>
  </div>
);

const ProjectCard = ({ project, onSelect, onDelete }) => (
  <div className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:border-indigo-200 transition-all flex flex-col overflow-hidden">
    <div className="p-5 flex-grow">
      <div className="flex justify-between items-start mb-3">
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
          project.status === 'Completed' ? 'bg-green-100 text-green-700' :
          project.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {project.status}
        </span>
        <button onClick={() => onDelete(project.id)} className="text-gray-300 hover:text-red-500 transition-colors">
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      <h3 className="text-lg font-poppins font-bold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors cursor-pointer" onClick={() => onSelect(project)}>
        {project.name}
      </h3>
      <p className="text-xs font-roboto text-gray-500 line-clamp-2 mb-4">
        {project.description}
      </p>

      <div className="flex items-center space-x-2 mb-4 text-xs">
        <div className="h-7 w-7 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-700 font-bold">
          {getLeadName(project.teamLeadId).charAt(0)}
        </div>
        <p className="text-gray-700 font-medium font-poppins">{getLeadName(project.teamLeadId)}</p>
      </div>

      <div className="space-y-1.5">
        <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase">
          <span>Progress</span>
          <span className="text-gray-700">{project.progress}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
          <div className="bg-indigo-600 h-full transition-all" style={{ width: `${project.progress}%` }} />
        </div>
      </div>
    </div>

    <div className="px-5 py-3 bg-gray-50 border-t border-gray-50 flex justify-between items-center text-[11px] font-medium text-gray-500">
      <div className="flex items-center">
        <CalendarDaysIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
        {formatDate(project.endDate)}
      </div>
      <button onClick={() => onSelect(project)} className="text-indigo-600 font-bold flex items-center hover:text-indigo-800">
        Manage <ArrowRightIcon className="h-3 w-3 ml-1" />
      </button>
    </div>
  </div>
);

const ProjectModal = ({ isOpen, onClose, project, onSave, isEditing, availableEmployees }) => {
  const [form, setForm] = useState({
    name: '', description: '', startDate: getTodayDate(), endDate: getTodayDate(),
    status: 'Pending', progress: 0, teamLeadId: '', team: [], updates: [],
    newMemberId: '', newUpdateNote: '',
  });

  useEffect(() => {
    if (isOpen) {
      setForm({
        ...form,
        ...(project || {}),
        name: project?.name || '',
        team: project?.team || [],
        updates: project?.updates || [],
        progress: project?.progress || 0,
        status: project?.status || 'Pending'
      });
    }
  }, [project, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!form.name || !form.teamLeadId) return alert("Missing Project Name or Lead");
    onSave({ ...form, id: project?.id || `P${Date.now()}` });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-poppins font-bold text-gray-800">
            {isEditing ? 'Edit Project' : 'New Project'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">&times;</button>
        </div>

        <div className="flex-grow overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Title</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-1 focus:ring-indigo-500 outline-none" placeholder="Project name" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Lead</label>
              <select value={form.teamLeadId} onChange={e => setForm({...form, teamLeadId: e.target.value})} className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-1 focus:ring-indigo-500 outline-none">
                <option value="">Select...</option>
                {availableEmployees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-1 focus:ring-indigo-500 outline-none">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-2">
          <button onClick={onClose} className="px-4 py-2 rounded text-xs font-bold text-gray-500 hover:text-gray-700">Cancel</button>
          <button onClick={handleSave} className="px-6 py-2 bg-indigo-600 text-white rounded text-xs font-bold hover:bg-indigo-700 transition">
            {isEditing ? 'Save Changes' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ProjectTrackingApp() {
  const [projects, setProjects] = useState(INITIAL_PROJECTS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  const filteredProjects = useMemo(() => {
    return projects
      .filter(p => filterStatus === 'All' || p.status === filterStatus)
      .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || getLeadName(p.teamLeadId).toLowerCase().includes(searchTerm.toLowerCase()));
  }, [projects, searchTerm, filterStatus]);

  return (
    <div className="min-h-screen bg-gray-50/50 font-roboto text-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold font-poppins text-gray-900 tracking-tight">Project Hub</h1>
            <p className="text-xs text-gray-500">Track and manage your company assignments.</p>
          </div>
          <button onClick={() => { setIsEditing(false); setCurrentProject(null); setIsModalOpen(true); }} className="flex items-center space-x-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-indigo-700 transition">
            <PlusCircleIcon className="h-4 w-4" />
            <span>New Project</span>
          </button>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard title="Total" value={projects.length} icon={ClipboardDocumentListIcon} colorClass="text-indigo-600" />
          <MetricCard title="Active" value={projects.filter(p => p.status === 'In Progress').length} icon={ClockIcon} colorClass="text-blue-600" />
          <MetricCard title="Done" value={projects.filter(p => p.status === 'Completed').length} icon={CheckCircleIcon} colorClass="text-green-600" />
          <MetricCard title="People" value={new Set(projects.flatMap(p => [p.teamLeadId, ...p.team.map(m => m.id)])).size} icon={UsersIcon} colorClass="text-teal-600" />
        </div>

        <div className="bg-white p-3 rounded-xl border border-gray-200 flex flex-col md:flex-row gap-3 mb-6 items-center">
          <div className="flex-grow relative w-full">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search projects..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-gray-50 border border-gray-100 rounded-lg py-1.5 pl-9 pr-3 text-xs outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
          <div className="flex bg-gray-50 p-1 rounded-lg border border-gray-100 shrink-0">
            {['All', 'Pending', 'In Progress', 'Completed'].map(status => (
              <button key={status} onClick={() => setFilterStatus(status)} className={`px-3 py-1 rounded text-[10px] font-bold transition ${filterStatus === status ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                {status}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map(p => (
            <ProjectCard key={p.id} project={p} onSelect={project => { setCurrentProject(project); setIsEditing(true); setIsModalOpen(true); }} onDelete={id => setProjects(projects.filter(p => p.id !== id))} />
          ))}
        </div>

        <ProjectModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          project={currentProject} 
          isEditing={isEditing} 
          availableEmployees={MOCK_EMPLOYEES} 
          onSave={data => {
            setProjects(prev => isEditing ? prev.map(p => p.id === data.id ? data : p) : [...prev, data]);
          }} 
        />
      </div>
    </div>
  );
}